import invariant from 'invariant';
import { useMemo } from 'react';

import type { ApiFilestackImageWithTextData } from '#/api/types/ApiFilestackImageData';
import { FilestackImage } from '#/components/FilestackImage/FilestackImage';
import { randomInt } from '#/utils/functional/number/randomInt';
import {
  getLocalStorageRawItem,
  setLocalStorageRawItem,
} from '#/utils/localStorage';

type Props = {
  postId: string;
  placeholderImages: ApiFilestackImageWithTextData[];
  alt: string;
  height: number;
  width: number;
  round?: boolean;
  expandSize?: boolean;
  qaId?: string;
};

export function PlaceholderImage({
  postId,
  placeholderImages,
  alt,
  height,
  width,
  round,
  expandSize,
  qaId,
}: Props) {
  invariant(
    typeof window !== 'undefined',
    'Placeholder image uses local storage (browser)',
  );

  const localStorageKey = `placeholderImg:${postId}`;

  const storedHandle = getLocalStorageRawItem(localStorageKey);
  const randomItem = useMemo(
    () => placeholderImages[randomInt(placeholderImages.length)],
    [placeholderImages],
  );

  const image =
    placeholderImages.find((i) => i.handle === storedHandle) ??
    (() => {
      setLocalStorageRawItem(localStorageKey, randomItem.handle);
      return randomItem;
    })();

  return (
    <FilestackImage
      data={image}
      alt={image?.altText ?? alt}
      height={height}
      width={width}
      round={round}
      expandSize={expandSize}
      qaId={qaId}
    />
  );
}
